/**
 * validation.js - Zod 기반 입력 데이터 검증 스키마
 * 
 * 보안 강화를 위한 클라이언트 사이드 입력 검증
 * 모든 사용자 입력은 API 호출 전에 이 스키마로 검증되어야 합니다.
 */

import { z } from 'zod';

/**
 * 이메일 검증 스키마
 */
export const EmailSchema = z
  .string()
  .min(1, '이메일을 입력해주세요.')
  .max(255, '이메일은 255자 이하여야 합니다.')
  .email('올바른 이메일 형식을 입력해주세요.');

/**
 * OTP 코드 검증 스키마 (6자리 숫자)
 */
export const OTPSchema = z
  .string()
  .length(6, 'OTP 코드는 6자리여야 합니다.')
  .regex(/^\d{6}$/, 'OTP 코드는 숫자만 입력 가능합니다.');

/**
 * 날짜 검증 스키마 (YYYY-MM-DD 형식)
 */
export const DateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '날짜 형식이 올바르지 않습니다. (YYYY-MM-DD)')
  .refine(
    (date) => {
      const parsed = new Date(date);
      return !isNaN(parsed.getTime());
    },
    { message: '유효한 날짜를 입력해주세요.' }
  );

/**
 * 시간(분) 검증 스키마
 * 양수 정수만 허용, 최대 1440분(24시간)
 */
export const MinutesSchema = z
  .union([
    z.string().transform((val) => {
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error('숫자를 입력해주세요.');
      return Math.round(num);
    }),
    z.number().int()
  ])
  .refine((val) => val > 0, { message: '시간은 0보다 커야 합니다.' })
  .refine((val) => val <= 1440, { message: '시간은 1440분(24시간)을 초과할 수 없습니다.' });

/**
 * 계약 ID 검증 스키마
 * 사용자가 직접 입력하지 않는 필드이므로, 숫자 또는 문자열 형식 모두 허용
 * 예: 123 (숫자) 또는 "THE-C-2024-086" (문자열)
 */
export const ContractIdSchema = z
  .union([
    z.number().int().positive(),
    z.string().min(1, '계약 ID는 비어있을 수 없습니다.'),
    z.undefined().transform(() => {
      throw new Error('계약을 선택해주세요.');
    }),
    z.null().transform(() => {
      throw new Error('계약을 선택해주세요.');
    })
  ]);

/**
 * 계약 객체 검증 스키마
 */
export const ContractSchema = z.object({
  contract_id: ContractIdSchema,
  contract_title: z.string().min(1, '계약명이 필요합니다.'),
  company_code: z.string().optional(),
  company_name: z.string().optional(),
}).nullable();

/**
 * 투입시간 로그 생성 스키마
 * 사용자가 직접 입력하는 필드(시간, 날짜, 설명)에만 집중
 */
export const TimelogCreateSchema = z.object({
  log_date: DateSchema,
  company_code: z.string().min(1, '업체 코드가 필요합니다.').optional(), // 서버 필수 필드 (선택적로 처리하여 유연성 확보)
  contract_id: ContractIdSchema, // 계약 선택으로 자동 설정되므로 타입만 확인
  job_category: z.string().max(100, '업무 구분은 100자 이하여야 합니다.').optional().nullable(), // 업무 구분 필드 추가
  work_description: z.string().max(500, '업무내용은 500자 이하여야 합니다.').optional().default(''),
  input_minute: MinutesSchema, // 사용자 입력 필드 - 엄격한 검증
  input_description: z.string().max(1000, '비고는 1000자 이하여야 합니다.').optional(),
  notes: z.string().max(1000, '비고는 1000자 이하여야 합니다.').optional(),
}).refine((data) => {
  // work_description 또는 input_description 중 하나는 필수 (사용자 입력 검증)
  return (data.work_description && data.work_description.trim().length > 0) || 
         (data.input_description && data.input_description.trim().length > 0);
}, {
  message: '업무내용 또는 비고를 입력해주세요.',
  path: ['work_description']
});

/**
 * 투입시간 로그 업데이트 스키마
 * 사용자가 직접 입력하는 필드에만 집중
 */
export const TimelogUpdateSchema = z.object({
  log_date: DateSchema.optional(),
  contract_id: ContractIdSchema.optional(), // 숫자 또는 문자열 모두 허용
  job_category: z.string().max(100, '업무 구분은 100자 이하여야 합니다.').optional().nullable(), // 업무 구분 필드 추가
  work_description: z.string().max(500, '업무내용은 500자 이하여야 합니다.').optional(),
  input_minute: MinutesSchema.optional(), // 사용자 입력 필드
  input_description: z.string().max(1000, '비고는 1000자 이하여야 합니다.').optional(),
  notes: z.string().max(1000, '비고는 1000자 이하여야 합니다.').optional(),
}).passthrough(); // 추가 필드는 그대로 통과 (서버에서 처리)

/**
 * OTP 요청 스키마
 */
export const OTPRequestSchema = z.object({
  email: EmailSchema,
});

/**
 * OTP 검증 스키마
 */
export const OTPVerifySchema = z.object({
  email: EmailSchema,
  otp_code: OTPSchema,
});

/**
 * 배치 투입시간 로그 생성 스키마
 */
export const TimelogBatchCreateSchema = z.array(TimelogCreateSchema).min(1, '최소 1개 이상의 로그가 필요합니다.');

/**
 * To-Do 생성 스키마
 */
export const TodoCreateSchema = z.object({
  company_code: z.string().optional().nullable(),
  contract_id: z.union([
    z.string().min(1, '계약 ID는 비어있을 수 없습니다.'),
    z.number().int().positive(),
  ]).optional().nullable(),
  job_category: z.string().max(100, '업무 구분은 100자 이하여야 합니다.').optional().nullable(),
  note_1: z.string().max(1000, '업무 상세내용은 1000자 이하여야 합니다.').optional().nullable(),
  note_2: z.string().max(1000, '비고는 1000자 이하여야 합니다.').optional().nullable(),
  status: z.enum(['active', 'completed']).default('active'),
  due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '날짜 형식이 올바르지 않습니다. (YYYY-MM-DD)').optional().nullable(),
}).refine((data) => {
  // note_1 (업무 상세내용)은 필수
  return data.note_1 && data.note_1.trim().length > 0;
}, {
  message: '업무 상세내용을 입력해주세요.',
  path: ['note_1']
});

/**
 * To-Do 업데이트 스키마
 */
export const TodoUpdateSchema = z.object({
  company_code: z.string().optional().nullable(),
  contract_id: z.union([
    z.string().min(1, '계약 ID는 비어있을 수 없습니다.'),
    z.number().int().positive(),
  ]).optional().nullable(),
  job_category: z.string().max(100, '업무 구분은 100자 이하여야 합니다.').optional().nullable(),
  note_1: z.string().max(1000, '업무 상세내용은 1000자 이하여야 합니다.').optional().nullable(),
  note_2: z.string().max(1000, '비고는 1000자 이하여야 합니다.').optional().nullable(),
  status: z.enum(['active', 'completed']).optional(),
  due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '날짜 형식이 올바르지 않습니다. (YYYY-MM-DD)').optional().nullable(),
}).passthrough(); // 추가 필드는 그대로 통과 (서버에서 처리)

/**
 * 검증 헬퍼 함수
 * @param {z.ZodSchema} schema - Zod 스키마
 * @param {any} data - 검증할 데이터
 * @returns {{ success: boolean, data?: any, error?: string }} 검증 결과
 */
export function validateData(schema, data) {
  try {
    const result = schema.safeParse(data);
    
    if (result.success) {
      return { success: true, data: result.data };
    } else {
      // 에러 발생 시에만 로그 출력
      const firstError = result.error?.errors?.[0];
      if (firstError) {
        console.error('❌ [Zod 검증 실패]', {
          field: firstError.path?.join('.') || '알 수 없음',
          message: firstError.message,
          code: firstError.code,
          input: data
        });
      }
      
      return {
        success: false,
        error: firstError?.message || '입력 데이터가 올바르지 않습니다.',
      };
    }
  } catch (error) {
    console.error('💥 [Zod 검증 예외]', error);
    return {
      success: false,
      error: error.message || '검증 중 예외가 발생했습니다.',
    };
  }
}

/**
 * 검증 헬퍼 함수 (에러 메시지 배열 반환)
 * @param {z.ZodSchema} schema - Zod 스키마
 * @param {any} data - 검증할 데이터
 * @returns {{ success: boolean, data?: any, errors?: string[] }} 검증 결과
 */
export function validateDataWithAllErrors(schema, data) {
  // 데이터가 유효한지 먼저 확인
  if (data === undefined || data === null) {
    console.error('❌ [Zod 검증] 데이터가 없습니다.');
    return {
      success: false,
      errors: ['검증할 데이터가 없습니다.'],
    };
  }

  try {
    const result = schema.safeParse(data);
    
    if (result.success) {
      return { success: true, data: result.data };
    } else {
      // 에러 발생 시에만 상세 로그 출력
      const errors = result.error?.errors?.map((err) => {
        const fieldPath = err.path?.join('.') || '알 수 없음';
        return `[${fieldPath}] ${err.message}`;
      }) || ['검증 중 오류가 발생했습니다.'];
      
      console.error('❌ [Zod 검증 실패]', {
        errors: result.error?.errors?.map((err, index) => ({
          index: index + 1,
          field: err.path?.join('.') || '알 수 없음',
          message: err.message,
          code: err.code,
        })),
        summary: errors
      });
      
      return {
        success: false,
        errors,
      };
    }
  } catch (error) {
    console.error('💥 [Zod 검증 예외]', error);
    return {
      success: false,
      errors: [error.message || '검증 중 예외가 발생했습니다.'],
    };
  }
}

